import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { api, describeError } from '@/lib/api';
import { useIdempotencyKey } from '@/lib/idempotency';
import { useStepUpDialog, type StepUpProof } from '@/lib/step-up';
import { stepUpTarget } from '@/lib/passkeys';
import {
  AGENT_WORK_ORDERS_URL,
  cancelAgentWorkOrder,
  createAgentWorkOrder,
  describeWorkOrderError,
  getAgentWorkOrderOptions,
  isFormValid,
  kindLabel,
  listAgentWorkOrders,
  minimumForm,
  type AgentWorkOrderDto,
  type AgentWorkOrderForm,
  type AgentWorkOrderOptions,
} from '@/lib/agent-work-orders';
import { AgentWorkOrderFields, AgentWorkOrderTable } from '@/components/AgentWorkOrderFields';
import { StepUpDialog } from '@/components/StepUpDialog';

type TenantHeader = { id: string; tradeName: string; isActive: boolean };

/**
 * "Delegar ao agente" (change platform-mcp-writes, tarefa 1.17): ordem de serviço para o agente de
 * escrita agir nesta loja. A loja é a do endereço da página (`tenantId`), nunca um nome digitado: o
 * nome aparece só para conferência. O formulário começa no mínimo (1 dia, 1 efeito, validade
 * padrão) e o modo é proposta: cada ação do agente ainda espera aprovação de uma pessoa. A criação
 * pede o código do autenticador (ou passkey) e leva `Idempotency-Key`.
 */
export function TenantAgentWorkOrderPage() {
  const { tenantId } = useParams();
  const [tenant, setTenant] = useState<TenantHeader | null>(null);
  const [options, setOptions] = useState<AgentWorkOrderOptions | null>(null);
  const [form, setForm] = useState<AgentWorkOrderForm | null>(null);
  const [items, setItems] = useState<AgentWorkOrderDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const idempotency = useIdempotencyKey();

  const loadList = useCallback(async () => {
    if (!tenantId) return;
    setItems(await listAgentWorkOrders(tenantId));
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    Promise.all([
      api.get<TenantHeader>(`/platform/tenants/${tenantId}`),
      getAgentWorkOrderOptions(),
      listAgentWorkOrders(tenantId),
    ])
      .then(([tenantResponse, loadedOptions, loadedItems]) => {
        if (cancelled) return;
        setTenant(tenantResponse.data);
        setOptions(loadedOptions);
        setForm(minimumForm(loadedOptions));
        setItems(loadedItems);
      })
      .catch((err) => !cancelled && setError(describeError(err)));
    return () => { cancelled = true; };
  }, [tenantId]);

  async function create(proof: StepUpProof, payload: AgentWorkOrderForm) {
    if (!tenantId) return;
    const key = idempotency.keyFor(AGENT_WORK_ORDERS_URL, { tenantId, ...payload });
    let created: AgentWorkOrderDto;
    try {
      created = await createAgentWorkOrder(tenantId, payload, proof, key);
      idempotency.settle();
    } catch (err) {
      idempotency.settle(err);
      throw err;
    }
    setNotice(`Ordem de serviço ${created.id} criada: ${kindLabel(created.kind)}, até ${created.maxDays ?? 0} dia(s).`);
    if (options) setForm(minimumForm(options, payload.kind));
    await loadList();
  }

  const stepUp = useStepUpDialog(create, (err) => setError(describeWorkOrderError(err)), {
    isValid: (payload) => (options ? isFormValid(payload, options) : false),
    passkeyTarget: () => (tenantId ? stepUpTarget('agentWorkOrderCreate', tenantId) : null),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!form || !options || !isFormValid(form, options) || stepUp.open) return;
    setError(null);
    setNotice(null);
    stepUp.start({ ...form });
  }

  async function cancel(item: AgentWorkOrderDto) {
    if (!window.confirm(`Cancelar a ordem de serviço ${item.id}? O agente deixa de poder usá-la.`)) return;
    setError(null);
    setNotice(null);
    setCancelling(item.id);
    try {
      await cancelAgentWorkOrder(item.id);
      setNotice(`Ordem de serviço ${item.id} cancelada.`);
      await loadList();
    } catch (err) {
      setError(describeWorkOrderError(err));
    } finally {
      setCancelling(null);
    }
  }

  const back = <Link to={`/tenants/${tenantId}`} className="backlink">← Tenant</Link>;
  if (!tenant || !options) {
    return (
      <div className="page">
        {back}
        {error ? <div className="error-banner">{error}</div> : <p>Carregando…</p>}
      </div>
    );
  }

  const canSubmit = form !== null && isFormValid(form, options);

  return (
    <div className="page" style={{ maxWidth: 960 }}>
      {back}
      <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 24, fontWeight: 800, margin: 0, letterSpacing: '-0.02em' }}>
        Delegar ao agente
      </h2>

      {error && <div className="error-banner" role="alert">{error}</div>}
      {notice && <div className="info-banner" role="status">{notice}</div>}

      <form className="card" onSubmit={submit} aria-label="Nova ordem de serviço">
        <div className="kv" style={{ marginBottom: 12 }}>
          <div className="k">Loja</div>
          <div>
            {tenant.tradeName} <span className="mono muted" data-testid="work-order-tenant-id">{tenant.id}</span>
          </div>
        </div>
        <p className="muted" style={{ marginTop: 0 }}>
          A ordem de serviço autoriza o agente a agir só nesta loja, só no tipo escolhido e até os máximos abaixo.
          No modo proposta, cada ação do agente continua esperando a aprovação de uma pessoa.
        </p>

        {options.kinds.length === 0 ? (
          <div className="info-banner">Seu papel não tem permissão para nenhum tipo de ordem de serviço.</div>
        ) : form && (
          <>
            <AgentWorkOrderFields options={options} form={form} onChange={setForm} disabled={stepUp.open} />
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="submit" className="btn" disabled={!canSubmit || stepUp.open}>
                Delegar ao agente
              </button>
            </div>
          </>
        )}
      </form>

      <div className="card">
        <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>Ordens de serviço desta loja</h3>
        <AgentWorkOrderTable items={items} onCancel={(item) => void cancel(item)} busyId={cancelling} />
      </div>

      {stepUp.open && (
        <StepUpDialog
          title="Confirmar a ordem de serviço"
          description={
            form ? (
              <>
                {kindLabel(form.kind)} em <strong>{tenant.tradeName}</strong>, até {form.maxDays} dia(s), {form.maxEffects}{' '}
                efeito(s), válida por {form.validForHours} h.
              </>
            ) : null
          }
          confirmLabel="Delegar"
          busy={stepUp.busy}
          error={stepUp.error}
          onSubmit={stepUp.submit}
          onCancel={stepUp.cancel}
          onPasskey={stepUp.passkeyAvailable ? stepUp.submitPasskey : undefined}
        />
      )}
    </div>
  );
}
